import React, { useState, useEffect, forwardRef, useRef } from 'react'
import styled from 'styled-components'
import type { DataNode } from 'antd/es/tree'
import { MenuItem, Dropdown } from 'react-bootstrap'
import Tree from '../Tree'
import i18n from 'live/services/i18n'
import useOutsideClick from './../../hooks/useOutsideClick'
import Search from '../../Search'
import { find, getKeys } from '../../utils/AntdTreeUtils'
import globalNotification from 'live/services/global-notification'

const Toggle = forwardRef((props: any, ref) => {
    const handleClick = e => {
        props.onClick()
    }

    return (
        <WrapperToogle open={props.isOpen} onClick={handleClick}>
            <div className="wrapper-toggle-title">
                <div>{i18n(props.title)}</div>
                <i className="align-end fas fa-caret-down" />
            </div>

            <div className="wrapper-toggle-description">
                {i18n('Section')}:<span className="wrapper-toggle-description-value">{i18n(props.description)}</span>
            </div>
        </WrapperToogle>
    )
})

type IProps = {
    title: string
    description: string
    data: DataNode[]
    checkeds?: React.Key[]
    apply(keys: React.Key[]): void
}

const DropdownTree = ({ title = '--', description = '--', checkeds = [], data = [], apply }: IProps) => {
    const openDropdown = () => {
        setOpen(!open)
    }

    const treeCallback = (keys: React.Key[]) => {
        setCheckeds([...keys])
    }

    const _apply = () => {
        if (_checkeds.length === 0) {
            const msg = (
                <WarnMsg>
                    <div style={{ marginBottom: 4 }}>
                        <i className="fas fa-exclamation-triangle"></i>
                    </div>

                    <div>
                        <div className="warn-msg-title">
                            {i18n('It is not possible to continue without a route or a section selected.')}
                        </div>

                        <div className="warn-msg-subtitle">
                            {i18n('Please define a section or a route to view its information.')}
                        </div>
                    </div>
                </WarnMsg>
            )

            globalNotification.notify(msg, 'info', 10, false)
        } else {
            checkedsAppliedCopy.current = _checkeds
            setChekedsApplied(_checkeds)
            setOpen(false)
            if (apply) apply(_checkeds)
        }
    }

    const cancel = () => {
        search('')
        setOpen(false)
        setCheckeds(checkedsAppliedCopy.current)
    }

    const selectAll = () => {
        const keys = getKeys(data)
        setCheckeds([...keys])
    }

    const deselectAll = () => {
        setCheckeds([])
    }

    const search = (query: string) => {
        setQuery(query)

        if (!query || query.length == 0) {
            setData(data)
        } else {
            const dataFiltered: DataNode[] = find(query, data)
            setData(dataFiltered)
        }
    }

    const ref = useRef(null)
    const checkedsAppliedCopy = useRef<any>({ current: [] })
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [treeEmptyMsg, setTreeEmptyMsg] = useState('')
    const [_checkeds, setCheckeds] = useState<React.Key[]>(checkeds)
    const [_data, setData] = useState<DataNode[]>(data)
    const [checkedsApplied, setChekedsApplied] = useState<React.Key[]>(checkeds)

    useEffect(() => {
        if (data && data.length > 0 && query.length > 0) {
            setTreeEmptyMsg('No routes or sections were found with the search performed.')
        } else {
            setTreeEmptyMsg('There are no routes or sections available for selection')
        }
    }, [data, query])

    useEffect(() => {
        const handleClickOutside = event => {
            if (ref.current && !ref.current.contains(event.target)) {
                cancel()
            }
        }

        document.body.addEventListener('click', handleClickOutside)

        return () => {
            document.body.removeEventListener('click', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        checkedsAppliedCopy.current = checkeds
        setChekedsApplied(checkeds)
    }, [checkeds])

    return (
        <CustomDropdown id={`dropdown-tree`} open={open} onToggle={() => { }}>
            <Toggle bsRole="toggle" isOpen={open} title={title} description={description} onClick={openDropdown} />

            <Dropdown.Menu>
                <CustomMenuItem>
                    <div ref={ref}>
                        {' '}
                        {/* USED AS REFERENCE TO CLOSE DROPDOWN */}
                        <WrapperSearch>
                            <Search inputValue={query} placeholder="Search" onSearch={search} />
                        </WrapperSearch>
                        {_data.length > 0 && (
                            <>
                                <WrapperCheckboxActions>
                                    <a href="" onClick={selectAll}>
                                        {i18n('Select All')}
                                    </a>
                                    <a href="" onClick={deselectAll}>
                                        {i18n('Deselect All')}
                                    </a>
                                </WrapperCheckboxActions>

                                <Separator />
                            </>
                        )}
                        <div className="wrapper-tree">
                            <Tree data={_data} checked={_checkeds} callback={treeCallback} emptyMsg={treeEmptyMsg} selectAll={selectAll} deselectAll={deselectAll} />
                        </div>
                        <WrapperActions>
                            <div>
                                <button onClick={e => _apply()} className="btn btn-primary">
                                    {i18n('Apply')}
                                </button>
                            </div>
                            <div>
                                <a href="" onClick={e => cancel()}>
                                    {i18n('Cancel')}
                                </a>
                            </div>
                        </WrapperActions>
                    </div>
                </CustomMenuItem>
            </Dropdown.Menu>
        </CustomDropdown>
    )
}

export default DropdownTree

const CustomDropdown = styled(Dropdown)`
    width: 100%;

    .dropdown-menu {
        width: 100%;
        padding: 0;
        margin: 0;
        padding: 0 !important;
        top: 100% !important;
        left: 0 !important;
        border-radius: 0 !important;
        z-index: 100;
        border-radius: 3px !important;
    }

    .dropdown-menu > li > a:hover,
    .dropdown-menu > li > a:focus {
        outline: none;
        text-decoration: none;
        background-color: ${({ theme }) => theme['gray-0']};
    }

    .dropdown-menu > li > a,
    .dropdown-menu > .menu-item {
        padding: 0 !important;
    }

    input.search {
        border: none;
        /* background: red; */
    }

    .widget-container .dropdown-menu:not(.widget-menu-edit-options) {
    }
`

const WrapperToogle = styled.div`
    padding: 16px;
    cursor: pointer;
    border-radius: 3px;
    background-color: ${({ theme, open }) => (open ? theme['blue-200'] : theme['gray-0'])};

    &:hover {
        border-radius: 3px;
        background-color: ${({ theme }) => theme['gray-100']};
    }

    .wrapper-toggle-title {
        cursor: pointer;
        display: flex;
        font-weight: 600;
        align-items: center;
        text-transform: uppercase;
        color: ${({ theme }) => theme['blue-700']};
        border-bottom: 1px solid ${({ theme }) => theme['gray-200']};
        padding-bottom: 5px;
        margin-bottom: 5px;

        div {
            flex: 1;
        }

        i {
            font-size: 14px;
        }
    }

    .wrapper-toggle-description {
        cursor: pointer;
    }

    .wrapper-toggle-description-value {
        color: ${({ theme }) => theme['gray-800']};
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        margin-left: 5px;
    }
`

const CustomMenuItem = styled(MenuItem)`
    max-height: 384px;
    overflow: hidden;

    .wrapper-tree {
        padding: 16px;
        max-height: 249px;
        overflow-y: auto;

        &::-webkit-scrollbar {
            width: 6px;
            height: 6px;
        }

        &::-webkit-scrollbar-track {
            border-radius: 10px;
        }

        &::-webkit-scrollbar-thumb {
            background: ${({ theme }) => theme['gray-500']};
            border-radius: 10px;
        }
    }
`

const WrapperSearch = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme['gray-300']};
`

const WrapperActions = styled.div`
    display: flex;
    align-items: center;
    padding: 8px 16px;
    text-align: center;
    border-top: 1px solid ${({ theme }) => theme['gray-300']};

    div {
        flex: 1;

        &:first-of-type {
            margin-right: 8px;
        }
    }

    button {
        width: 100%;
        border: none;
    }
`

const WrapperCheckboxActions = styled.div`
    padding: 16px;

    a {
        &:first-child {
            margin-right: 16px;
        }
    }

    .ant-tree-title {
        color: ${({ theme }) => theme['gray-800']};
        font-family: ${({ theme }) => theme['font-semibold']};
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;
    }
`

const Separator = styled.div`
    height: 1px;
    width: 90%;
    margin: 0 auto;
    background: ${({ theme }) => theme['gray-100']};
`

const WarnMsg = styled.div`
    display: flex;

    div:first-of-type {
        margin-right: 8px;
    }

    .warn-msg-title {
        color: ${({ theme }) => theme['gray-800']};
        font-family: ${({ theme }) => theme['font-semibold']};
        font-size: 16px;
        font-style: normal;
    }

    .warn-msg-subtitle {
        color: ${({ theme }) => theme['gray-800']};
        font-family: ${({ theme }) => theme['font-semibold']};
        font-size: 14px;
        font-style: italic;
    }
`
